// Lazy wrapper around @sentry/react so it never gets pulled into the SSR
// module graph. All exports are safe to import from SSR-rendered files.

type SentryReact = typeof import("@sentry/react");

let sentry: SentryReact | undefined;
let installed = false;

export async function initSentryClient(opts: {
  dsn: string;
  release?: string;
  environment?: string;
  sampleRate?: number;
  tracesSampleRate?: number;
  incident?: boolean;
}) {
  if (installed || typeof window === "undefined") return;
  if (!opts.dsn) return;
  installed = true;
  sentry = await import("@sentry/react");
  sentry.init({
    dsn: opts.dsn,
    release: opts.release,
    environment: opts.environment,
    sampleRate: opts.sampleRate ?? 1,
    tracesSampleRate: opts.tracesSampleRate ?? 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    initialScope: {
      tags: {
        runtime: "browser",
        ...(opts.incident ? { incident: "true" } : {}),
      },
    },
  });
}

export async function disableSentryClient() {
  if (!sentry) return;
  try {
    await sentry.close(2000);
  } catch {
    // ignore
  }
  sentry = undefined;
  installed = false;
}

export function isSentryInstalled() {
  return installed;
}

export function setSentryUser(user: { id: string; email?: string } | null) {
  if (!sentry) return;
  sentry.setUser(user ? { id: user.id, email: user.email } : null);
}

export function setSentryRoute(route: string) {
  if (!sentry) return;
  sentry.setTag("route", route);
}

export function captureClientException(error: unknown, extra?: Record<string, unknown>) {
  if (!sentry) return;
  sentry.captureException(error, { extra });
}
